import React from 'react'
import { connect } from 'react-redux'
import { DataViewerUtil, HistoryPaginator, ResponsiveView } from '@leverege/molten/data-viewers'
import { Config } from '@leverege/plugin'
import { DateTimeRangePicker, Flex } from '@leverege/ui-elements'
import { Toolbar } from '@leverege/ui-plugin'
import { ValuesCache, ValueCache } from '@leverege/value-cache'

import { ItemDelegate } from '@leverege/molten-core'
import { DataSourceFactory } from '@leverege/ui-attributes'
import { Selection } from '@leverege/ui-redux'
import EventsEnabledGroupDelegate from './EventsEnabledGroupDelegate'
import Model from './EventsViewModel'

import S from './EventsView.css'
import { getQueryHandlerResults } from './EventsUtil'
import EventsEnabledItemDelegate from './EventsEnabledItemDelegate'

class EventsView extends React.Component {
  constructor( props ) {
    super( props )

    this.state = {
      page : 0,
      // eslint-disable-next-line react/no-unused-state
      loadingPages : false
    }
    this.refreshTime = Config.get( 'GroupScreen', 'refreshTime', 30000 )
    this.caches = new ValuesCache()
    this.paginator = new HistoryPaginator( { onPageChange : this.onSetPage } )
  }

  componentDidMount() {
    this.loadData()
  }

  componentWillUnmount() {
    this.unmounted = true
  }

  componentDidUpdate( prevProps, prevState ) {
    const { settings } = this.props
    const { settings : pSettings } = prevProps
    const { page } = this.state
    const { page : pPage } = prevState

    let range = Model.getRange( settings )
    let pRange = Model.getRange( pSettings )

    range = DateTimeRangePicker.getRange( range )
    pRange = DateTimeRangePicker.getRange( pRange )

    if (
      range.start.valueOf() !== pRange.start.valueOf() ||
      range.end.valueOf() !== pRange.end.valueOf() ||
      page !== pPage
    ) {
      this.loadData()
    }
  }

  loadData = async () => {
    clearTimeout( this.timer )
    if ( this.unmounted ) {
      return
    }
    const { settings, dataViewer, relationship } = this.props
    const { page } = this.state

    const perPage = Model.getPerPage( settings )

    let range = Model.getRange( settings )
    range = DateTimeRangePicker.getRange( range )

    const options = getQueryHandlerResults( { 
      objectType : relationship.objectType,
      path : relationship.path,
      client : dataViewer.type,
      relationship,
      caches : this.caches
    }, this.getContext() )

    await this.queryEvents( {
      page,
      perPage,
      order : [ [ '$time', 'ASC' ] ],
      range,
      ...options,
    } )

    if ( this.unmounted !== true ) {
      this.timer = setTimeout( this.loadData, this.refreshTime )
    }
  }

  async queryEvents( { range, page, perPage, where, order, ...rest } ) {
    const { dispatch, delegate, dataViewerInstanceId, useParent, settings } = this.props

    const getAllPages = settings.getAllPages !== false
    const currentQueryId = this.queryId
    const d = useParent ? delegate.parent() : delegate

    // this.setState( { loadingPages : true } )
    if ( getAllPages ) {
      let p = 0
      let hasMoreData = true

      while ( hasMoreData ) {
        if ( this.queryId !== currentQueryId ) {
          break
        }
        // eslint-disable-next-line no-await-in-loop
        const { items } = await dispatch(
          d.eventsDelegate.search( {
            query : {
              start : range.start,
              end : range.end,
              perPage,
              startIndex : p * perPage,
              order : order || [ [ '$time', 'ASC' ] ],
              where,
              ...rest
            },
            queryName : dataViewerInstanceId.current,
          } ),
        )

        p += 1

        if ( items.length < perPage ) {
          hasMoreData = false
        }
      }
    } else {
      await dispatch(
        d.eventsDelegate.search( {
          query : {
            start : range.start,
            end : range.end,
            perPage,
            startIndex : page * perPage,
            order : order || [ [ '$time', 'ASC' ] ],
            where,
            ...rest
          },
          queryName : dataViewerInstanceId.current,
        } ),
      )
    }
    // this.setState( { loadingPages : false } )
  }

  getContext( dataViewerType ) {
    return {
      client : 'EventsView', // : View.CLIENT,
      dataViewerType,
      clientProps : {
        ...this.props,
      },
    }
  }

  rendererHeader = ( dataViewerModel ) => {
    if ( dataViewerModel == null ) {
      return null
    }
    const {
      relationship : { path },
      objectType,
      inCard,
    } = this.props

    const actionBarMatchContext = {
      use : 'actionBar',
      client : 'EventsView',
      dataViewerType : dataViewerModel.type,
      objectType,
      path,
    }
    const filterBarMatchContext = {
      use : 'filterBar',
      client : 'EventsView',
      dataViewerType : dataViewerModel.type,
      objectType,
      path,
    }
    const context = this.getContext( dataViewerModel.type )

    const content = (
      <Flex className={inCard ? undefined : S.toolbars} variant="tableToolbar">
        <Toolbar
          className={S.toolbar}
          variant="filterBar|actionBar"
          matchContext={filterBarMatchContext}
          context={context}/>
        <Toolbar
          className={S.toolbar}
          variant="actionBar"
          prefer="icon"
          matchContext={actionBarMatchContext}
          context={context}/>
      </Flex>
    )

    return <DataViewerUtil.HeaderToolbar inCard={inCard} content={content} />
  }

  onSetPage = ( { page, perPage } ) => {
    const { settings, onSettingsChange, settingsData } = this.props
    const cPerPage = Model.getPerPage( settings )
    
    this.setState( { page } )
    if ( cPerPage !== perPage ) {
      onSettingsChange( { value : Model.setPerPage( settings, perPage ), data : settingsData } )
    }
    // UserSettings.set( settingsPath, Model.setPerPage( model, perPage ) )
  }

  createDataItems = ( result ) => {
    const { delegate } = this.props
    const { items = [] } = result

    return {
      status : result?.status || null,
      loading : false,
      perPage : result.perPage,
      page : 0,
      count : result?.total || 0,
      items : items.map( ( item ) => {
        const type = `${item.type}.event`
        const dataSource = DataSourceFactory.get( type ) || { getData : ( ) => null }
        const ref = `${delegate.eventsDelegate.getInterfaceUrl()}/${item.deviceId}.event`

        const obj = { id : item.id, type, ref }
        const data = dataSource.getData( obj )
        obj.data = data

        return obj
      } )
    }
  }

  render() {
    const { result } = this.props
    const context = this.getContext()

    const dataItems = this.caches.calc( 'items', this.createDataItems, result )

    this.paginator.setData( dataItems )

    return (
      <ResponsiveView
        {...this.props}
        queryType="events"
        context={context}
        eventsPaginator={this.paginator}
        useContentView
        contentHeader={this.rendererHeader}/>
    )
  }
}

const delegateCache = new ValueCache()

export default connect( ( state, ownProps ) => {
  const {
    selectionKey,
    rolloverKey,
    targetKey,
    selected : pSelected,
    targeted : pTargeted,
    rollover : pRollover,
    delegate : pDelegate,
    dataViewerInstanceId,
    settings
  } = ownProps

  const { delegate, useParent } = delegateCache.calc( ( pDelegate, pSettings ) => {
    let delegate, useParent = false
    if ( pDelegate instanceof ItemDelegate ) {
      delegate = new EventsEnabledItemDelegate( {
        match : pDelegate.match,
        relationship : pDelegate.relationship
      } )
    } else {
      useParent = settings.useParent != null ? settings.useParent : true
      delegate = new EventsEnabledGroupDelegate( {
        match : pDelegate.match,
        relationship : pDelegate.relationship,
      } )
    }
    
    return {
      delegate,
      useParent
    }
  }, pDelegate, settings )

  const getAll = true

  const d = useParent ? delegate.parent() : delegate
  const queryData = d
    .eventsDelegate.getList( state, dataViewerInstanceId.current, { getAll } )

  return {
    result : queryData || {},
    delegate,
    useParent,
    selected : pSelected || Selection.get( state, selectionKey ),
    targeted : pTargeted || Selection.get( state, targetKey ),
    rollover : pRollover || Selection.get( state, rolloverKey ),
  }
} )( EventsView )
